import { Helmet } from 'react-helmet-async';

const siteUrl = 'https://solvedmedia.co.uk';

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Solved Media",
  "url": siteUrl,
  "logo": `${siteUrl}/favicon.webp`,
  "description": "Data, Leads, AI & Consultancy without the bullshit. Honest, direct, and fully committed to delivering real results.",
  "sameAs": [
    "https://twitter.com/solved_media"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "url": `${siteUrl}/contact`
  }
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Solved Media",
  "url": siteUrl,
  "logo": `${siteUrl}/favicon.webp`,
  "image": `${siteUrl}/favicon.webp`,
  "description": "Data, Leads, AI & Consultancy. Honest, direct, and fully committed to delivering real results.",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "GB"
  },
  "priceRange": "££",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "17:30"
  }
};

const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Solved Media Services",
  "description": "Professional business services including data, leads, AI, automations, websites and consultancy",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Service",
        "name": "Consumer Data",
        "description": "UK consumer data with full transparency. Validated, suppressed and delivered properly.",
        "url": `${siteUrl}/data`,
        "provider": {
          "@type": "Organization",
          "name": "Solved Media"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Service",
        "name": "AI Solutions",
        "description": "Smarter AI solutions tailored to your business. Lead nurturing, process automation, and customer engagement.",
        "url": `${siteUrl}/ai`,
        "provider": {
          "@type": "Organization",
          "name": "Solved Media"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Service",
        "name": "Automations",
        "description": "Work smarter with intelligent automations. Email sequences, CRM integration, and workflow automation.",
        "url": `${siteUrl}/automations`,
        "provider": {
          "@type": "Organization",
          "name": "Solved Media"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 4,
      "item": {
        "@type": "Service",
        "name": "Website Build",
        "description": "Professional websites for £495. Free hosting, free SSL, delivered in 5-7 days.",
        "url": `${siteUrl}/website-build`,
        "provider": {
          "@type": "Organization",
          "name": "Solved Media"
        },
        "offers": {
          "@type": "Offer",
          "price": "495",
          "priceCurrency": "GBP"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 5,
      "item": {
        "@type": "Service",
        "name": "Consultancy",
        "description": "When you need an extra brain, not another pitch. Honest, impartial advice with practical solutions.",
        "url": `${siteUrl}/consultancy`,
        "provider": {
          "@type": "Organization",
          "name": "Solved Media"
        }
      }
    }
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What does Solved Media actually do?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We generate leads, automate conversations, and re-engage your database using AI—without the usual industry nonsense. Whether it's social media ads, AI chatbots, or data-driven outreach, we focus on getting results, not just selling you a fancy dashboard."
      }
    },
    {
      "@type": "Question",
      "name": "How does your AI chatbot work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Think of it as me, but available 24/7 and slightly less sarcastic. It handles customer enquiries, qualifies leads, books appointments, and follows up—without needing coffee breaks or sick days. It's designed to sound natural and actually be useful."
      }
    },
    {
      "@type": "Question",
      "name": "How much does it cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Depends on what you need. Some services are pay-per-lead, some are custom-built solutions, and some pricing (like list data) varies based on volume and quality. No hidden fees, no unnecessary upsells—just tell us what you're after, and we'll give you a straight answer."
      }
    },
    {
      "@type": "Question",
      "name": "Can you help my business?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "If you deal with customers and want more of them, probably. We work with a wide range of industries, from finance to home improvements, and our AI solutions are adaptable. If we think we can't help, we'll tell you—because wasting your time (or ours) isn't how we do business."
      }
    },
    {
      "@type": "Question",
      "name": "Who do you typically work with?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We work with businesses that want more customers and better systems, not shortcuts. From SMEs to established brands, the common thread is a willingness to be honest about what is not working and improve it properly."
      }
    }
  ]
};

export const StructuredData = () => {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(servicesSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};
